import { gql as untypedGql } from "@apollo/client";

/**
 * Codegen excludes these hand-written documents, so they carry no result
 * types. Apollo Client 3 typed an untyped document's data as `any`; Apollo
 * Client 4 types it as `unknown`, which broke every reader. This keeps the
 * Apollo Client 3 typing until the documents are retired (separate work).
 *
 * @type {(...args: Parameters<typeof untypedGql>) => import("@apollo/client").TypedDocumentNode<any, any>}
 */
const gql = untypedGql;

import * as fragments from "./fragments";

export const CONFIRM_ACCREDITATION = gql`
	mutation ConfirmAccreditation($id: ID!, $country: ID!, $file: Upload!) {
		confirmAccreditation(form: { id: $id, country: $country, file: $file }) {
			...investingEntityFields
		}
	}
	${fragments.INVESTING_ENTITY}
`;

export const CREATE_SESSION = gql`
	mutation CreateSession($email: String!, $password: String!) {
		createSession(account: { email: $email, password: $password }) {
			account {
				...accountFields
			}
			features {
				...featureFlagFields
			}
			session {
				...sessionFields
			}
		}
	}
	${fragments.ACCOUNT}
	${fragments.FEATURE_FLAG}
	${fragments.SESSION}
`;

export const CREATE_SESSION_FROM_TOKEN = gql`
	mutation CreateSessionFromToken($token: String!) {
		createSessionFromToken(account: { token: $token }) {
			account {
				...accountFields
			}
			features {
				...featureFlagFields
			}
			session {
				...sessionFields
			}
		}
	}
	${fragments.ACCOUNT}
	${fragments.FEATURE_FLAG}
	${fragments.SESSION}
`;

export const COMPLETE_BENEFICIAL_OWNER_CONTACT_DETAILS = gql`
	mutation CompleteBeneficialOwnerContactDetails(
		$contactDetails: [BeneficialOwnerContactDetailsInput!]!
	) {
		completeBeneficialOwnerContactDetails(
			form: { contactDetails: $contactDetails }
		) {
			...investingEntityFields
		}
	}
	${fragments.INVESTING_ENTITY}
`;

export const DESTROY_SESSION = gql`
	mutation DestroySession($token: JWT!) {
		destroySession(session: { token: $token }) {
			message
			statusCode
		}
	}
`;

export const RESEND_EMAIL_CONFIRMATION = gql`
	mutation ResendEmailConfirmation($email: String!) {
		resendEmailConfirmation(account: { email: $email }) {
			message
			statusCode
		}
	}
`;

export const RESET_PASSWORD = gql`
	mutation ResetPassword($email: String!) {
		resetPassword(account: { email: $email }) {
			message
			statusCode
		}
	}
`;

export const SEND_CHANGE_PASSWORD_EMAIL = gql`
	mutation SendChangePasswordEmail {
		sendChangePasswordEmail {
			id
			email
		}
	}
`;

export const UPDATE_PROFILE = gql`
	mutation UpdateProfile(
		$timeZone: String
		$callingCode: String
		$phoneNumber: String
		$preferredName: String
		$autocompleteAddress: AutocompleteAddressInput
		$manualAddress: ManualAddressInput
	) {
		updateProfile(
			form: {
				timeZone: $timeZone
				callingCode: $callingCode
				phoneNumber: $phoneNumber
				preferredName: $preferredName
				autocompleteAddress: $autocompleteAddress
				manualAddress: $manualAddress
			}
		) {
			...accountFields
		}
	}
	${fragments.ACCOUNT}
`;

export const UPLOAD_ADDRESS_VERIFICATION_DOCUMENT = gql`
	mutation UploadAddressVerificationDocument(
		$addressCity: String
		$addressCountry: String
		$addressLine1: String
		$addressPlaceId: String
		$addressPostCode: String
		$addressRawSearch: String
		$addressSuburb: String
		$file: Upload!
	) {
		uploadAddressVerificationDocument(
			form: {
				addressCity: $addressCity
				addressCountry: $addressCountry
				addressLine1: $addressLine1
				addressRawSearch: $addressRawSearch
				addressSuburb: $addressSuburb
				addressPlaceId: $addressPlaceId
				addressPostCode: $addressPostCode
				file: $file
			}
		) {
			...accountFields
		}
	}
	${fragments.ACCOUNT}
`;

export const UPLOAD_INVESTING_ENTITY_DOCUMENTS = gql`
	mutation UploadInvestingEntityDocuments(
		$investingEntityId: ID!
		$files: [Upload!]!
	) {
		uploadInvestingEntityDocuments(
			form: { investingEntityId: $investingEntityId, files: $files }
		) {
			investingEntity {
				id
				documents {
					...remoteAssetFields
				}
			}
		}
	}
	${fragments.REMOTE_ASSET}
`;

export const ADD_INVESTING_ENTITY_DOCUMENTS = gql`
	mutation AddInvestingEntityDocuments(
		$investingEntityId: ID!
		$category: DocumentCategory!
		$files: [Upload!]!
	) {
		addInvestingEntityDocuments(
			form: {
				investingEntityId: $investingEntityId
				category: $category
				files: $files
			}
		) {
			id
			documents {
				...remoteAssetFields
			}
		}
	}
	${fragments.REMOTE_ASSET}
`;

export const PROVIDE_SOURCE_OF_FUNDS = gql`
	mutation ProvideSourceOfFunds(
		$id: ID!
		$sourceOfFunds: SourceOfFundsSource!
		$document: Upload!
		$supportingInformation: String
	) {
		provideSourceOfFunds(
			form: {
				id: $id
				sourceOfFunds: $sourceOfFunds
				document: $document
				supportingInformation: $supportingInformation
			}
		) {
			...investingEntityFields
		}
	}
	${fragments.INVESTING_ENTITY}
`;
